/**
 * Client-rendered app routes: everything that is a real page of the app but is
 * NOT prerendered (login, password reset, dashboards, admin screens, newsletter
 * landing pages). The host serves these from the empty 200.html shell with
 * status 200; every other path that is not a real file gets status 404 (see
 * public/_redirects), so unknown URLs are real 404s instead of soft 404s.
 *
 * Patterns use the _redirects syntax (Netlify-style, also read by Cloudflare Pages): "/x" matches exactly /x (and /x/), "/x/*"
 * matches everything below /x (list "/x" too when /x itself is a page). src/seo/notFound.test.ts fails when a route in
 * App.tsx is not covered here, or when a pattern has no matching 200 rule in
 * public/_redirects, so adding an app route means adding it in all three places.
 */
export const APP_ROUTE_PATTERNS: readonly string[] = [
  '/login',
  '/logout',
  '/forgot-password',
  '/forgot-password/*',
  '/users/*',
  '/subscription-expired',
  '/company-deactivated',
  '/subscription/*',
  '/desktop/*',
  '/account/*',
  '/admin/*',
  '/analytics/*',
  '/cash',
  '/tanks',
  '/shiftreview',
  '/employeedeficit',
  '/employeecreditcard',
  '/newsletter/confirmed',
  '/newsletter/unsubscribed',
  '/en/newsletter/confirmed',
  '/en/newsletter/unsubscribed',
];

/** Whether a _redirects-style pattern matches a path (trailing slash ignored). */
export function matchesPattern(pattern: string, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2);
    return path.startsWith(`${prefix}/`);
  }
  return path === pattern;
}

/** Whether a path is a client-rendered app route (served with 200, never "not found"). */
export function isAppRoute(pathname: string): boolean {
  return APP_ROUTE_PATTERNS.some((pattern) => matchesPattern(pattern, pathname));
}
